import { useMemo, useState } from "react";
import { message } from "antd";
import { FileData, FileUrl, FileValidationResult, formatToMimeType, FileUploadProps, MAX_FILE_SIZE, MAX_FILE_COUNT } from "./constants";

export function useFileUpload({
	maxFileSize,
	multiple,
	defaultUrl,
	maxFileCount,
	allowedExtensions,
	dimensions,
	onChange
}: FileUploadProps) {
	const [fileList, setFileList] = useState<FileData[]>([]);
	const [uploadProgress, setUploadProgress] = useState(0);
	const defaultFile = useMemo<FileUrl | null>(() => {
		if (!defaultUrl) {
			return null;
		}

		const fileName = defaultUrl.split("/").pop() || "default_file";
		const extension = fileName.split(".").pop()?.toLowerCase() || "";
		const mimeType = formatToMimeType[extension] || "application/octet-stream";

		const defaultFile: FileUrl = {
			name: fileName,
			type: mimeType,
			url: defaultUrl,
		};

		return defaultFile;
	}, [defaultUrl]);

	const fileSizeLimit = (() => {
		const parsed = Number(maxFileSize);
		return Number.isFinite(parsed) && parsed > 0 ? parsed : MAX_FILE_SIZE;
	})();

	const fileCountLimit = (() => {
		if (!multiple) {
			return 1;
		}

		if (typeof maxFileCount === "number" && maxFileCount > 0) {
			return maxFileCount;
		}
		return MAX_FILE_COUNT;
	})();

	const validateImageDimensions = (img: HTMLImageElement): boolean => {

		if (!dimensions || dimensions.length === 0) return true;

		const isValid = dimensions.some(
			dim => dim.width === img.width && dim.height === img.height
		);

		return isValid;
	};


	const allowedMimeTypes = allowedExtensions.map(ext => formatToMimeType[ext]);

	const validateFile = (file: File): FileValidationResult | null => {
		if (file.size > fileSizeLimit) {
			return { filename: file.name, message: "Dosya boyut sınırını aşıyor." };
		}

		if (!allowedMimeTypes.includes(file.type)) {
			return { filename: file.name, message: "Dosya formatı desteklenmiyor." };
		}

		return null;
	};

	const loadImageDimensions = (file: File): Promise<HTMLImageElement | null> => {
		return new Promise(resolve => {
			if (!file.type.startsWith("image/")) {
				resolve(null);
				return;
			}
			const img = new Image();
			const url = URL.createObjectURL(file);
			img.onload = () => {
				URL.revokeObjectURL(url);
				resolve(img);
			};
			img.onerror = () => {
				URL.revokeObjectURL(url);
				resolve(null);
			};
			img.src = url;
		});
	};

	const customUploadAsync = async (batchFileList: File[], file: File) => {
		const withinLimit = checkBatchLimit(batchFileList, file);

		if (!withinLimit) return false;

		const fileResult = await processFile(file);

		if (!fileResult) return false;

		if (batchFileList.length > 1 && batchFileList[0] === file) {
			message.success(`${batchFileList.length} dosya başarıyla yüklendi.`);
		}
		else if (batchFileList.length === 1) {
			message.success(`${file.name} dosyası başarıyla yüklendi.`);
		}

		return true;
	};

	const checkBatchLimit = (batchFileList: File[], file: File) => {
		if (!multiple) {
			return true;
		}

		if (fileList.length + batchFileList.length > fileCountLimit) {
			if (batchFileList[0] === file) {
				message.warning(`En fazla ${fileCountLimit} dosya yükleyebilirsiniz.`);
			}
			return false;
		}
		return true;
	};

	const processFile = async (file: File) => {
		if (!file) return false;

		const existingNames = new Set(multiple ? fileList.map(f => f.name) : []);

		const reason = validateFile(file);
		if (reason) {
			message.error(`${reason.filename} dosyası yüklenemedi: ${reason.message}`);
			return false;
		}

		if (existingNames.has(file.name)) {
			message.error(`${file.name} adlı dosya halihazırda yüklenmiş.`);
			return false;
		}

		const img = await loadImageDimensions(file);
		// An image the browser cannot decode (e.g. a renamed or broken file) has no size to check the required dimensions against.
		if (!img && dimensions?.length && file.type.startsWith("image/")) {
			message.error(`${file.name} dosyası yüklenemedi: Görsel okunamadı.`);
			return false;
		}
		if (img && !validateImageDimensions(img)) {
			message.error(`${file.name} dosyası için boyutlar geçersiz.`);
			return false;
		}

		const newFileData: FileData = {
			id: crypto.randomUUID(),
			name: file.name,
			size: file.size,
			type: file.type,
			url: URL.createObjectURL(file),
			file,
		};

		setFileList(prevList => {
			const updatedList = multiple ? [...prevList, newFileData] : [newFileData];
			if (onChange) onChange(updatedList);
			return updatedList;
		});
		setUploadProgress(100);
		return true;
	};

	const handleRemove = (file: FileData) => {
		setFileList(prevFileList => {
			const updatedFileList = prevFileList.filter(f => f.id !== file.id);
			if (onChange) onChange(updatedFileList.length > 0 ? updatedFileList : null);
			return updatedFileList;
		});
		URL.revokeObjectURL(file.url);
		message.success("Dosya başarıyla silindi");
	};

	const handleRemoveAll = () => {
		setFileList(prevFileList => {
			prevFileList.forEach(f => URL.revokeObjectURL(f.url));
			if (onChange) onChange(null);
			return [];
		});
		message.success("Tüm dosyalar başarıyla silindi");
		setUploadProgress(0);
	};

	return {
		fileList,
		defaultFile,
		customUploadAsync,
		uploadProgress,
		allowedMimeTypes,
		handleRemove,
		handleRemoveAll,
	};
}
